/**
 * Setting up a supplier.
 *
 * WHY PAYMENT TERMS SIT WITH THE PHONE NUMBER
 * -------------------------------------------
 * They are the two things anybody looks a supplier up for: how to reach them,
 * and when the invoice is due. Terms buried on a second screen get left at
 * whatever the default was, and then every invoice from that supplier is
 * chased on the wrong day.
 *
 * WHY A SUPPLIER IS SWITCHED OFF RATHER THAN DELETED
 * --------------------------------------------------
 * Past orders and deliveries record who they came from. Removing the supplier
 * would leave a delivery that arrived from nobody, which is worse than a name
 * in the list that nobody orders from any more.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

export function SupplierForm({ initial = {}, onClose, onSaved }) {
  const isNew = !initial.id;

  const [name, setName] = useState(initial.name ?? '');
  const [contact, setContact] = useState(initial.contact_person ?? '');
  const [phone, setPhone] = useState(initial.phone ?? '');
  const [email, setEmail] = useState(initial.email ?? '');
  const [city, setCity] = useState(initial.city ?? '');
  const [address, setAddress] = useState(initial.address ?? '');
  const [taxId, setTaxId] = useState(initial.tax_id ?? '');
  const [terms, setTerms] = useState(
    initial.payment_terms_days != null ? String(initial.payment_terms_days) : ''
  );
  const [active, setActive] = useState(
    initial.is_active === undefined ? true : !!initial.is_active
  );

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        contactPerson: contact.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        city: city.trim() || null,
        address: address.trim() || null,
        taxId: taxId.trim() || null,
        paymentTermsDays: terms === '' ? null : Number(terms),
        isActive: active,
      };

      return isNew
        ? api.post('/purchasing/suppliers', body)
        : api.put(`/purchasing/suppliers/${initial.id}`, body);
    },
    onSuccess: onSaved,
  });

  const termsAreSilly = terms !== '' && Number(terms) < 0;
  const ready = name.trim() && !termsAreSilly;

  const field =
    'h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <h3 className="mb-3 font-medium text-ink">
        {isNew ? 'New supplier' : name || 'Change supplier'}
      </h3>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="sm:col-span-2">
          <span className="mb-1.5 block text-xs text-ink-3">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Pays in how many days</span>
          <input
            type="number"
            min="0"
            step="1"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            aria-label="Pays in how many days"
            className={`${field} text-right tabular-nums`}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Who to ask for</span>
          <input
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            aria-label="Who to ask for"
            className={field}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Phone</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            aria-label="Phone"
            inputMode="tel"
            className={field}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Email</span>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Email"
            inputMode="email"
            className={field}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">City</span>
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            aria-label="City"
            className={field}
          />
        </label>

        <label className="sm:col-span-2">
          <span className="mb-1.5 block text-xs text-ink-3">Address</span>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            aria-label="Address"
            className={field}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Tax number</span>
          <input
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
            aria-label="Tax number"
            className={field}
          />
        </label>
      </div>

      {termsAreSilly && (
        <p className="mt-2 text-sm text-warn">Payment terms cannot be a negative number of days.</p>
      )}

      {!isNew && (
        <label className="mt-3 flex items-center gap-2">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-5 w-5 rounded border-rule accent-brand-400"
          />
          <span className="text-sm text-ink-2">
            Still ordering from them
            {/* Not deleted: past deliveries record who they came from, and a
                delivery that arrived from nobody is worse than a stale name. */}
          </span>
        </label>
      )}

      {save.isError && (
        <p className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {save.error?.message ?? 'That did not work.'}
        </p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>
          {save.isPending ? 'Saving' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
